import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';
import { Truck, Plus, AlertTriangle, CheckCircle2, ClipboardList, ChevronRight, Settings2 } from 'lucide-react';
import TruckBoxDetail from './TruckBoxDetail';
import TruckBoxStandard from './TruckBoxStandard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/truck-boxes`;
const fmtD = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'never');

export default function TruckBoxes() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const [data, setData] = useState({ boxes: [], can_edit: false, mine: [] });
  const [open, setOpen] = useState(null);
  const [showStd, setShowStd] = useState(false);
  const [adding, setAdding] = useState(false);
  const [trucks, setTrucks] = useState([]);
  const [newTruck, setNewTruck] = useState('');
  const [newForeman, setNewForeman] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const r = await axios.get(API); setData(r.data); } catch { toast.error('Could not load truck boxes'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const id = params.get('truck');
    if (!id) { setOpen(null); return; }
    axios.get(`${API}/${id}`).then(r => setOpen(r.data)).catch(() => toast.error('Could not open that truck box'));
    // eslint-disable-next-line
  }, [location.search]);

  const openBox = (b) => navigate(`/tool-crib?tab=trucks&truck=${b.id}`, { replace: true });
  const closeBox = () => { setOpen(null); navigate('/tool-crib?tab=trucks', { replace: true }); load(); };

  const startAdd = async () => {
    setAdding(true);
    try { const r = await axios.get(`${API}/trucks`); setTrucks(r.data.trucks || []); } catch { toast.error('Could not load the fleet'); }
  };
  const create = async () => {
    if (!newTruck) return;
    try {
      const r = await axios.post(API, { truck_id: newTruck, foreman_name: newForeman.trim() || (trucks.find(t => t.id === newTruck)?.operator || '') });
      toast.success(`Box created for ${r.data.truck_name}`);
      setAdding(false); setNewTruck(''); setNewForeman('');
      openBox(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create'); }
  };

  if (open) return <TruckBoxDetail box={open} onBack={closeBox} onChanged={setOpen} />;

  return (
    <div className="space-y-4" data-testid="truck-boxes">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Truck className="w-5 h-5 text-sky-700" /> Truck boxes</h2>
          <p className="text-sm text-slate-500 mt-0.5">The standard tool set every service truck goes out with — assigned to a foreman, counted against the list.</p>
        </div>
        {data.can_edit && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowStd(true)} data-testid="truck-std-edit-btn" className="px-3 py-2.5 rounded-xl bg-slate-900/5 hover:bg-slate-900/10 text-slate-900 text-sm font-semibold flex items-center gap-1.5"><Settings2 className="w-4 h-4" /> Standard list</button>
            <button onClick={startAdd} data-testid="truck-box-new-btn" className="px-4 py-2.5 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-bold text-sm flex items-center gap-1.5"><Plus className="w-4 h-4" /> New truck box</button>
          </div>
        )}
      </div>

      {showStd && <TruckBoxStandard onClose={() => { setShowStd(false); load(); }} />}

      {adding && (
        <div className="rounded-xl border border-sky-200 bg-white p-4 flex flex-wrap items-end gap-3" data-testid="truck-box-new-form">
          <label className="text-xs text-slate-700 font-semibold">Truck
            <select value={newTruck} onChange={e => { setNewTruck(e.target.value); const t = trucks.find(x => x.id === e.target.value); if (t && !newForeman) setNewForeman(t.operator && t.operator !== 'SHOP TRUCK' ? t.operator : ''); }}
              data-testid="truck-box-new-truck" className="block mt-1 px-3 py-2.5 rounded-xl bg-white text-slate-900 text-sm min-w-[220px]">
              <option value="">Pick a truck…</option>
              {trucks.map(t => <option key={t.id} value={t.id} disabled={!!t.box_id}>{t.equipment_name}{t.operator ? ` — ${t.operator}` : ''}{t.box_id ? ' (has a box)' : ''}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-700 font-semibold">Foreman
            <input value={newForeman} onChange={e => setNewForeman(e.target.value)} placeholder="Foreman name" data-testid="truck-box-new-foreman"
              className="block mt-1 px-3 py-2.5 rounded-xl bg-white text-slate-900 text-sm w-56" />
          </label>
          <button onClick={create} disabled={!newTruck} data-testid="truck-box-new-save" className="px-4 py-2.5 rounded-xl bg-emerald-600 disabled:bg-slate-300 text-white font-bold text-sm">Create from standard list</button>
          <button onClick={() => setAdding(false)} className="px-3 py-2.5 rounded-xl bg-slate-900/5 text-slate-900 text-sm">Cancel</button>
        </div>
      )}

      {loading ? <p className="text-slate-500 text-sm">Loading…</p> : data.boxes.length === 0 ? (
        <p className="text-slate-500 text-sm py-8 text-center">No truck boxes yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {data.boxes.map(b => {
            const mine = data.mine.includes(b.id);
            const ok = b.shortage_count === 0 && b.unchecked_count === 0;
            return (
              <button key={b.id} onClick={() => openBox(b)} data-testid={`truck-box-card-${b.truck_id}`}
                className={`text-left rounded-xl border p-4 bg-white hover:bg-slate-50 transition-colors ${ok ? 'border-emerald-200' : 'border-amber-300'}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-bold text-slate-900 text-lg">{b.truck_id}</span>
                      <span className="text-sm text-slate-700 truncate">{b.truck_name}</span>
                      {mine && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-800">MY TRUCK</span>}
                    </div>
                    <div className="text-sm text-slate-800 mt-1" data-testid={`truck-box-foreman-${b.truck_id}`}>{b.foreman_name || <span className="text-slate-500 italic">no foreman</span>}</div>
                    <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500 mt-2">
                      <span className="inline-flex items-center gap-1"><ClipboardList className="w-3 h-3" />{b.item_count} items</span>
                      <span>counted {fmtD(b.last_checked_date)}{b.last_checked_by ? ` by ${b.last_checked_by}` : ''}</span>
                    </div>
                    <div className="mt-2">
                      {ok ? <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> All there</span>
                        : <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700" data-testid={`truck-box-short-${b.truck_id}`}><AlertTriangle className="w-3.5 h-3.5" /> {b.shortage_count} short{b.unchecked_count ? ` · ${b.unchecked_count} not counted` : ''}</span>}
                      {b.shortages.length > 0 && <div className="text-[11px] text-slate-500 mt-1 truncate">{b.shortages.map(s => `${s.name} ${s.counted}/${s.qty}`).join(' · ')}</div>}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-500 mt-1 shrink-0" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
